import {SequenceServerResponse} from 'constants/SequenceConstants';
import * as sequtil from 'molbio/SeqUtil';
import pick from 'util/pick';

import {GAP, IupacPartialMapping, NUCLEOTIDE_TYPE_TO_COMPLEMENT_GAP_TABLE} from './BioConstants';
import {Sequence, SequenceAttributes} from './Sequence';

export type GapSequenceJSON = Pick<
  SequenceServerResponse,
  | 'id'
  | 'bases'
  | 'annotations'
  | 'name'
  | 'circular'
  | 'color'
  | 'parts'
  | 'sequence_primers'
  | 'sequence_type'
  | 'translations'
>;

interface GapSequenceOptions {
  alignedBases?: string;
}

interface GapSequence {
  sync(): never;
}

class GapSequence extends Sequence {
  _gapToReal!: {[gapIndex: number]: number};
  _realToGap!: {[realIndex: number]: number};

  defaults(): Partial<SequenceAttributes> {
    return {
      ...super.defaults(),
      // Used for allowEdits, allowFeatureEdits, allowResidueEdits - alignments should be in writable mode.
      writable: true,
      // @ts-expect-error: missing required boolean attributes: `readable`, `appendable`, `admin`
      permissions: {
        polymerFeaturesEditable: true,
        polymerResiduesEditable: true,
        writable: true,
      },
    };
  }

  initialize(attributes: SequenceAttributes, options: GapSequenceOptions = {}): GapSequence {
    super.initialize(attributes, options);
    if (options.alignedBases != null) {
      this._withGaps(options.alignedBases);
    }
    this._realToGap = {};
    this._gapToReal = {};
    this._calculateIndices();
    return this.listenTo(this, 'change:bases', this._calculateIndices.bind(this));
  }

  // This logic is duplicated on the backend by
  // * benchling/molbio/analyses/alignment_analysis.py::gap_range_to_real_range
  // * benchling/molbio/analyses/alignment_analysis.py::real_range_to_gap_range
  _calculateIndices(): void {
    let index = 0;
    const iterable = this.bases();
    for (let i = 0; i < iterable.length; i++) {
      const c = iterable[i];
      this._gapToReal[i] = index;
      if (c !== GAP) {
        this._realToGap[index] = i;
        index += 1;
      }
    }
    this._realToGap[index] = this.bases().length;
    this._gapToReal[this.bases().length] = index;
  }

  _withGaps(alignedBases: string): void {
    const pattern = /-+/gi;

    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = pattern.exec(alignedBases))) {
      this.insertBases(match.index, match[0]);
    }
  }

  cloneWithoutGaps(): Sequence {
    const clone = this.clone();
    let end = 0;
    let baseIndex = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const ret = this.bases().slice(end).match(`${GAP}+`);
      if (ret == null) {
        break;
      }
      baseIndex += ret.index!;
      const start = ret.index! + end;
      end = start + ret[0].length;
      clone.deleteBases(baseIndex, baseIndex + ret[0].length);
    }

    const seq = new Sequence({...clone.attributes});
    seq.id = this.id;
    return seq;
  }

  stopAllListeners(): void {
    this.stopListening();
    this.trigger('stopAllListeners');
  }

  baseTable(): IupacPartialMapping {
    return NUCLEOTIDE_TYPE_TO_COMPLEMENT_GAP_TABLE[this.nucleotideType()];
  }

  sync(): never {
    throw new Error("Gap sequence can't sync!");
  }

  toSaveable(): Sequence {
    return this.cloneWithoutGaps();
  }

  gapToReal(pos: number): number {
    return this._gapToReal[pos];
  }

  realToGap(pos: number): number {
    return this._realToGap[pos];
  }

  realEndToGap(pos: number): number {
    if (pos === 0) {
      // Our mapping is from base to base (and not insert position to insert position),
      // so we need to grab the gap index of the last base, convert it to real, and add 1.
      const lastBaseGapIndex = this.gapToReal(this.length()) - 1;
      return sequtil.mod(this.realToGap(lastBaseGapIndex) + 1, this.length());
    }
    return this.realToGap(pos);
  }

  toJSON(options?: {omitSubpartFeatures: boolean}): GapSequenceJSON {
    // Matches sequence.py:Sequence.to_alignment_dict
    const ret = super.toJSON(options) as SequenceServerResponse;
    return pick(
      ret,
      'id',
      'bases',
      'annotations',
      'name',
      'circular',
      'color',
      'parts',
      'sequence_primers',
      'sequence_type',
      'translationGroups',
      'translations'
    );
  }
}

export default GapSequence;
